import { t, tx, type MessageKey } from '../i18n';
import type { DataEnvelope, Metric, Series } from '../types/analytics';
import type { AnalyticsSectionId, ExportSection, SectionDataMap } from './exportTypes';
import { sectionLabels } from './exportTypes';

export type CsvCell = string | number | null | undefined;
type CsvRow = CsvCell[];

/**
 * Human-readable metric names per section. Metric keys, the header row, units, availability and measurement tokens stay
 * in English so the file remains machine-readable; names, categories and groups follow the active language.
 */
const metricLabels: Record<AnalyticsSectionId, Record<string, MessageKey>> = {
  overview: {
    registeredUsers: 'v1.kpi.registeredUsers', verifiedUsers: 'v1.kpi.verifiedUsers', newRegistrations: 'v1.kpi.newRegistrations', newUsers: 'v1.kpi.newUsers', dau: 'v1.kpi.dau', wau: 'v1.kpi.wau', mau: 'v1.kpi.mau',
    receiptsScanned: 'v1.kpi.receiptsScanned', receiptUsers: 'v1.kpi.receiptUsers', receiptsPerReceiptUser: 'v1.kpi.receiptsPerReceiptUser',
    priceSearches: 'v1.kpi.priceSearches', priceComparisonUsers: 'v1.kpi.priceComparisonUsers', pantryUsers: 'v1.kpi.pantryUsers',
    budgetUsers: 'v1.kpi.budgetUsers', shoppingListUsers: 'v1.kpi.shoppingListUsers', crashFreeUsers: 'v1.kpi.crashFreeUsers',
    uninstalls: 'v1.kpi.uninstalls', crashFreeSessions: 'v1.kpi.crashFreeSessions', activeDevices: 'v1.kpi.activeDevices',
  },
  acquisition: {
    newInstalls: 'v1.acq.newInstalls', activeDevices: 'v1.acq.activeDevices', uninstalls: 'v1.label.uninstallsAny', uninstallsFromNewInstalls: 'v1.label.uninstallsThese', installsWithoutUninstall: 'v1.acq.withoutUninstall', activeFromNewInstalls: 'v1.label.activeFromNewInstalls', activeFromEarlierInstalls: 'v1.label.activeFromEarlierInstalls',
    rawNewInstalls: 'v1.acq.rawInstalls', rawUninstalls: 'v1.label.rawUninstalls', excludedInstalls: 'v1.acq.excluded',
  },
  activation: {}, retention: {
    d1: 'v1.ret.d1', d7: 'v1.ret.d7', d30: 'v1.ret.d30',
    activeDevices: 'v1.ret.activeDevices', newDevices: 'v1.ret.newDevices', returningDevices: 'v1.ret.returningDevices', stickiness: 'v1.ret.stickiness',
    devices: 'v1.ret.uninstallDevices', installs: 'v1.ret.uninstallInstalls', rate: 'v1.ret.signalRate',
  }, features: {},
  receipts: {
    processed: 'v1.label.processed', successful: 'v1.label.successful', failed: 'v1.label.failed', duplicates: 'v1.label.duplicates', successRate: 'v1.label.successRate',
    uniqueUsers: 'v1.label.uniqueReceiptUsers', receiptsPerUser: 'v1.label.receiptsPerUser', avgItemsPerReceipt: 'v1.label.avgItems',
    avgProcessingDuration: 'v1.label.avgProcessing', p50Latency: 'v1.label.processingP50', p95Latency: 'v1.label.processingP95',
    cacheHitRate: 'v1.label.cacheHitRate', enrichmentCalls: 'v1.label.enrichment',
  },
  'price-comparison': {
    totalSearches: 'v1.label.totalSearches', uniqueUsers: 'v1.label.uniqueUsers', searchesPerUser: 'v1.label.searchesPerUser', successfulSearches: 'v1.label.successfulSearches',
    noResultSearches: 'v1.label.noResultSearches', failures: 'v1.label.failures', noResultRate: 'v1.label.noResultRate', avgResultsReturned: 'v1.label.avgResults',
    avgRetailersPerResult: 'v1.label.avgChainCoverage', avgLatency: 'v1.label.avgLatency', p50Latency: 'v1.label.latencyP50', p95Latency: 'v1.label.latencyP95',
    browseRequests: 'v1.label.browseRequests', browsePagesLoaded: 'v1.label.browsePages', avgProductsPerBrowse: 'v1.label.avgProductsPerBrowse', loadMoreUsage: 'v1.label.loadMore',
  },
  costs: {
    totalCost: 'v1.label.totalCost', averageCostPerDay: 'v1.label.costPerDay', aiCost: 'v1.label.aiCost', costPerActiveUser: 'v1.label.costPerActiveUser',
    costPerReceipt: 'v1.label.costPerReceipt', costPerPriceSearch: 'v1.label.costPerSearch', aiCallsPerReceipt: 'v1.label.aiCallsPerReceipt',
    aiCalls: 'v1.label.aiCalls', aiCostPerCall: 'v1.label.aiCostPerCall',
  },
  reliability: {
    crashFreeUsers: 'v1.rel.crashFreeUsers', crashCount: 'v1.rel.crashCount', nonFatalErrors: 'v1.rel.nonFatal', receiptFailureRate: 'v1.rel.receiptFailure',
    priceSearchFailureRate: 'v1.rel.searchFailure', receiptLatency: 'v1.label.receiptAvgLatency', receiptP95Latency: 'v1.rel.receiptP95',
    priceSearchLatency: 'v1.label.searchAvgLatency', priceSearchP95Latency: 'v1.rel.searchP95',
    crashFreeSessions: 'v1.rel.crashFreeSessions', sessions: 'v1.rel.sessions', crashedSessions: 'v1.rel.crashedSessions', crashedUsers: 'v1.label.crashedUsers',
  },
};

const metricLabel = (section: AnalyticsSectionId, key: string) => {
  const message = metricLabels[section][key];
  return message ? t(message) : key;
};

export function escapeCsvCell(value: CsvCell): string {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function metricRow(category: string, key: string, metric: Metric, label: string): CsvRow {
  return [category, label || key, '', metric.availability === 'available' && metric.value !== null ? metric.value : 'N/A', metric.unit, metric.availability, metric.kind.toUpperCase()];
}

function metricRows(section: AnalyticsSectionId, category: string, record: Record<string, unknown>): CsvRow[] {
  return Object.entries(record).flatMap(([key, value]) => isMetric(value) ? [metricRow(category, key, value, metricLabel(section, key))] : []);
}

function seriesRows(series: Series | Series[]): CsvRow[] {
  const list = Array.isArray(series) ? series : [series];
  return list.flatMap((item) => item.points.map((point) => [t('v1.csv.trend'), tx(item.label), point.date, point.value === null ? 'N/A' : point.value, seriesUnit(item), point.value === null ? 'not_collected' : 'available', '']));
}

function seriesUnit(series: Series): string {
  if (/cost/i.test(`${series.id} ${series.label}`)) return 'currency';
  if (/latency|p50|p95/i.test(`${series.id} ${series.label}`)) return 'milliseconds';
  return 'count';
}

function isMetric(value: unknown): value is Metric {
  return typeof value === 'object' && value !== null && 'availability' in value && 'unit' in value && 'kind' in value;
}

function sectionRows(section: ExportSection): CsvRow[] {
  if (!section.envelope.data) return [[t('v1.csv.status'), t('v1.csv.dataset'), '', 'N/A', '', 'not_collected', '']];
  switch (section.id) {
    case 'overview': return overviewRows(section.envelope.data);
    case 'acquisition': return acquisitionRows(section.envelope.data);
    case 'activation': return activationRows(section.envelope.data);
    case 'retention': return retentionRows(section.envelope.data);
    case 'features': return featureRows(section.envelope.data);
    case 'receipts': return receiptRows(section.envelope.data);
    case 'price-comparison': return priceRows(section.envelope.data);
    case 'costs': return costRows(section.envelope.data);
    case 'reliability': return reliabilityRows(section.envelope.data);
  }
}

function overviewRows(data: SectionDataMap['overview']): CsvRow[] {
  return [
    ...metricRows('overview', t('v1.csv.kpi'), data.kpis), ...seriesRows([data.activeUsers, data.receiptUsage, data.costTrend]),
    ...data.featureAdoption.map((row) => [t('v1.csv.featureAdoption'), t('v1.csv.uniqueUsers'), tx(row.feature), row.users, 'count', 'available', '']),
  ];
}

function acquisitionRows(data: SectionDataMap['acquisition']): CsvRow[] {
  return [
    ...metricRows('acquisition', t('v1.csv.kpi'), data.kpis),
    ...seriesRows([data.installsTrend, data.rawInstallsTrend, data.activeTrend, data.uninstallsTrend]),
    ...Object.entries(data.breakdowns).flatMap(([dimension, rows]) => rows.flatMap((row) => [
      [t('v1.csv.breakdown'), t('v1.csv.breakdownInstalls', { dimension: tx(data.breakdownLabels[dimension]) ?? dimension }), tx(row.label), row.newInstalls, 'count', 'available', ''],
      [t('v1.csv.breakdown'), t('v1.csv.breakdownActive', { dimension: tx(data.breakdownLabels[dimension]) ?? dimension }), tx(row.label), row.activeDevices, 'count', 'available', ''],
    ])),
    ...data.exclusions.rules.map((rule) => [t('v1.csv.exclusionRule'), tx(rule.label), '', rule.devices, 'count', 'available', '']),
  ];
}

function activationRows(data: SectionDataMap['activation']): CsvRow[] {
  const installRows: CsvRow[] = (data.installFunnel?.segments ?? []).flatMap((segment) => [
    ...segment.steps.map((step): CsvRow => [t('v1.csv.installFunnel', { segment: segment.segment }), t('v1.csv.devices'), tx(step.label), step.users, 'count', 'available', '']),
    ...(['within24h', 'within7d', 'returned'] as const).flatMap((key): CsvRow[] => {
      const window = segment[key];
      const funnel = t('v1.csv.installFunnel', { segment: segment.segment });
      return window ? [[funnel, t('v1.csv.eligible', { window: key }), t('v1.csv.devices'), window.eligible, 'count', 'available', ''], [funnel, t('v1.csv.rate', { window: key }), t('v1.csv.devices'), window.rate.value ?? 'N/A', 'percent', window.rate.availability, tx(window.rate.reason) ?? '']] : [];
    }),
  ]);
  return [
    ...installRows,
    ...data.steps.flatMap((step) => [
      [t('v1.csv.registrationFunnel'), t('v1.csv.users'), tx(step.label), step.users, 'count', 'available', ''],
      [t('v1.csv.registrationFunnel'), t('v1.csv.conversionPrevious'), tx(step.label), step.conversionFromPrevious ?? 'N/A', 'percent', step.conversionFromPrevious === null ? 'not_applicable' : 'available', ''],
      [t('v1.csv.registrationFunnel'), t('v1.csv.conversionStart'), tx(step.label), step.conversionFromStart, 'percent', 'available', ''],
      [t('v1.csv.registrationFunnel'), t('v1.csv.dropOffPrevious'), tx(step.label), step.dropOffFromPrevious ?? 'N/A', 'percent', step.dropOffFromPrevious === null ? 'not_applicable' : 'available', ''],
    ]),
  ];
}

function retentionRows(data: SectionDataMap['retention']): CsvRow[] {
  return [
    ...metricRows('retention', t('v1.csv.kpi'), data.headline),
    ...(data.cohorts.map((cohort) => [t('v1.csv.cohort'), t('v1.csv.cohortSize'), tx(cohort.cohort), cohort.size, 'count', 'available', ''])),
    ...(data.activity ? metricRows('retention', t('v1.csv.activity'), data.activity as unknown as Record<string, unknown>) : []),
    ...(data.uninstalls ? metricRows('retention', t('v1.csv.uninstallSignals'), data.uninstalls as unknown as Record<string, unknown>) : []),
    ...data.cohorts.flatMap((cohort) => data.periodLabels.map((period, index) => [
      t('v1.csv.cohort'), t('v1.csv.periodRetention', { period }), tx(cohort.cohort), cohort.periods[index] ?? 'N/A', 'percent', cohort.periods[index] === null ? 'not_collected' : 'available', '',
    ])),
  ];
}

function featureRows(data: SectionDataMap['features']): CsvRow[] {
  return [
    ...data.features.flatMap((feature) => [
      [t('v1.csv.feature'), t('v1.csv.uniqueUsers'), tx(feature.name), feature.uniqueUsers, 'count', 'available', ''],
      [t('v1.csv.feature'), t('v1.csv.activeShare'), tx(feature.name), feature.activeUserShare, 'percent', 'available', ''],
      [t('v1.csv.feature'), t('v1.csv.eventCount'), tx(feature.name), feature.eventCount, 'count', 'available', ''],
      [t('v1.csv.feature'), t('v1.csv.eventsPerUser'), tx(feature.name), feature.eventsPerUser, 'ratio', 'available', ''],
      [t('v1.csv.feature'), t('v1.csv.repeatUsers'), tx(feature.name), feature.repeatUsers, 'count', 'available', ''],
      [t('v1.csv.feature'), t('v1.csv.repeatRate'), tx(feature.name), feature.repeatRate, 'percent', 'available', ''],
    ]),
    ...seriesRows(data.trends),
  ];
}

function receiptRows(data: SectionDataMap['receipts']): CsvRow[] {
  return [
    ...metricRows('receipts', t('v1.csv.kpi'), data as unknown as Record<string, unknown>),
    ...seriesRows([data.receiptsTrend, ...data.outcomeTrends, ...data.latencyTrend]),
    ...data.retailers.map((row) => [t('v1.csv.retailerDistribution'), t('v1.csv.receipts'), tx(row.retailer), row.receipts, 'count', 'available', '']),
    ...data.retailers.map((row) => [t('v1.csv.retailerDistribution'), t('v1.csv.share'), tx(row.retailer), row.share, 'percent', 'available', '']),
  ];
}

function priceRows(data: SectionDataMap['price-comparison']): CsvRow[] {
  return [...metricRows('price-comparison', t('v1.csv.kpi'), data as unknown as Record<string, unknown>), ...seriesRows([data.searchesTrend, ...data.outcomeTrends, ...data.latencyTrend])];
}

function costRows(data: SectionDataMap['costs']): CsvRow[] {
  return [
    ...metricRows('costs', t('v1.csv.kpi'), data as unknown as Record<string, unknown>), ...seriesRows(data.dailyCost),
    ...data.byService.map((row) => [t('v1.csv.costByService'), t('v1.csv.cost'), tx(row.service), Number(row.cost.toFixed(4)), data.currency, 'available', row.kind.toUpperCase()]),
  ];
}

function reliabilityRows(data: SectionDataMap['reliability']): CsvRow[] {
  return [
    ...metricRows('reliability', t('v1.csv.kpi'), data as unknown as Record<string, unknown>),
    ...seriesRows([...data.errorsTrend, ...data.latencyTrend]),
    ...(data.performanceTraces ?? []).flatMap((trace) => [
      metricRow(t('v1.csv.performanceTrace'), 'count', trace.count, t('v1.csv.traceCount', { trace: trace.trace })),
      metricRow(t('v1.csv.performanceTrace'), 'p50', trace.p50Latency, t('v1.csv.traceP50', { trace: trace.trace })),
      metricRow(t('v1.csv.performanceTrace'), 'p95', trace.p95Latency, t('v1.csv.traceP95', { trace: trace.trace })),
    ]),
  ];
}

export type SourceKind = 'mock' | 'actual+estimated' | 'estimated' | 'actual' | 'none';

/** What the figures of a section are: mock, measured, modelled, both, or nothing available. */
export function sourceKind(envelope: DataEnvelope<unknown>): SourceKind {
  if (envelope.meta.source === 'mock') return 'mock';
  const kinds = new Set<string>();
  const visit = (value: unknown): void => {
    if (!value || typeof value !== 'object') return;
    if (isMetric(value) && value.availability === 'available') kinds.add(value.kind);
    Object.values(value).forEach(visit);
  };
  visit(envelope.data);
  const hasActual = kinds.has('actual');
  const hasEstimated = kinds.has('estimated');
  return hasActual && hasEstimated ? 'actual+estimated' : hasEstimated ? 'estimated' : hasActual ? 'actual' : 'none';
}

/** The source kind as a label in the active language. */
export function sourceKindLabel(kind: SourceKind): string {
  if (kind === 'mock') return t('v1.report.mockSource');
  if (kind === 'actual+estimated') return t('v1.report.actualEstimated');
  if (kind === 'estimated') return t('v1.report.kind.estimated');
  if (kind === 'actual') return t('v1.report.kind.actual');
  return t('common.na');
}

function sourceLabel(envelope: DataEnvelope<unknown>): string {
  return sourceKindLabel(sourceKind(envelope));
}

export function buildCsvExport(section: ExportSection): { filename: string; content: string } {
  const { range, generatedAt } = section.envelope.meta;
  const coverage: CsvRow[] = (section.envelope.meta.sources ?? []).map((source) => [
    t('v1.csv.sourceCoverage'), source.id, coverageText(source), '', '', source.status === 'ready' ? 'available' : 'not_collected', source.kind.toUpperCase(),
  ]);
  const metadata: CsvRow[] = [
    [t('v1.csv.report'), 'Aruha Analytics'], [t('v1.csv.section'), sectionLabels[section.id]], [t('v1.csv.dateRange'), t('v1.csv.rangeValue', { start: range.start, end: range.end })],
    [t('v1.csv.timezone'), range.timezone], [t('v1.csv.generatedAt'), generatedAt], [t('v1.csv.dataSource'), sourceLabel(section.envelope)],
    ...coverage, [t('v1.csv.privacy'), t('v1.csv.privacyValue')], [],
    ['Category', 'Metric', 'Date / group', 'Value', 'Unit', 'Availability', 'Measurement'],
  ];
  const content = '\uFEFF' + [...metadata, ...sectionRows(section)].map((row) => row.map(escapeCsvCell).join(',')).join('\r\n');
  return { filename: `aruha-${section.id}-${range.start}_${range.end}.csv`, content };
}

function coverageText(source: NonNullable<DataEnvelope<unknown>['meta']['sources']>[number]): string {
  if (source.status !== 'ready') return t('v1.csv.notCollected');
  const start = source.earliestDate ? t('v1.report.from', { date: source.earliestDate }) : t('v1.report.startUnknown');
  const complete = source.latestCompleteDate ? t('v1.report.completeThrough', { date: source.latestCompleteDate }) : t('v1.report.completeUnknown');
  return `${start}; ${complete}`;
}

export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export { sourceLabel };
